import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import { Box, Stack, type SxProps, type Theme } from '@mui/material';
import { ChatBox, type ChatBoxProps, type ChatBoxSlotProps } from '@mui/x-chat';

import type {
  ChatMessage,
  ConversationalFormExplanation,
  ConversationalFormOption,
} from '@karehero/models';

import {
  noopChatAdapter,
  useMuiChatMessages,
  voicePartData,
  filePartData,
  cardPartData,
  menuPartData,
} from '@/pwa/hooks/useMuiChatMessages';
import {
  FailedMessageNote,
  type FailedMessageNoteProps,
} from './FailedMessageNote';
import {
  ABOVE_DIM_OVERLAY_Z_INDEX,
  DimOverlay,
} from '@/pwa/atoms/DimOverlay/DimOverlay';
import { GARDENIA_REPLIES_CLASS } from '@/theme/mui/components';
import { arrivalSx } from './arrival';
import { AttachmentMessageContent } from './AttachmentMessageContent';
import { ChatCard } from './ChatCard';
import { ChatMenuMessage } from './ChatMenuMessage';
import { FileAttachmentBubble } from './FileAttachmentBubble';
import { PhotoAttachmentBubble } from './PhotoAttachmentBubble';
import { PlayableVoiceNote } from './PlayableVoiceNote';
import { TypingDots } from './TypingDots';
import {
  HELD_LEAVE_MS,
  HELD_RETURN_DELAY_MS,
  HELD_RETURN_MS,
  isStuckRow,
  LEAVE_ANIMATION,
  RETURN_ANIMATION,
  THREAD_ROW_ATTRIBUTE,
  useHeldCard,
  type HeldRow,
} from './useHeldCard';

const EmptySlot = () => null;

const isPhoto = (contentType: string) => contentType.startsWith('image/');

type DataPartRenderer = NonNullable<
  NonNullable<ChatBoxProps['partRenderers']>[`data-${string}`]
>;

export interface MessageThreadProps {
  messages: ChatMessage[];
  /** Shows typing dots where the next system message will land. */
  isTyping?: boolean;
  onOpenDocument?: (documentId: string) => void;
  getDocumentUrl?: (documentId: string) => Promise<string | undefined>;
  /** Omit when a message that did not send cannot be tried again. */
  onResend?: (messageId: string) => void;
  /** Rendered after the last message, inside the scroller. */
  footer?: ReactNode;
  /** Opens a card's lines to be corrected, for a card that offers it. */
  onEditCard?: (messageId: string) => void;
  /** Pins or unpins a card, for a card that offers it. */
  onPinCard?: (messageId: string) => void;
  /** Room after the last row, in px, for what floats over the thread's end. */
  endSpace?: number;
  /** Their replies in coral, or in gardenia as the 2.0 design has them. */
  replyTone?: ReplyTone;
  /** The menu message being asked, the one whose rows answer. */
  openMenuId?: string;
  onChooseFromMenu?: (option: ConversationalFormOption) => void;
  /** Changes a menu's answer, for a menu that offers it. */
  onChangeMenuAnswer?: (
    messageId: string,
    option: ConversationalFormOption,
  ) => void;
  /** Opens what a row that explains, rather than answers, has to say. */
  onExplain?: (explanation: ConversationalFormExplanation) => void;
  /** Whether voice notes show their words. */
  isVoiceTextShown?: boolean;
  /** Shows or hides voice notes' words; omit to offer no words. */
  onToggleVoiceText?: () => void;
  /** A card held at the top of the chat once it is scrolled to, in its place in the thread. */
  heldCardId?: string;
  /** Dims the chat around the held card, so it and the bar are what is left; omit to never dim it. */
  isHeldDimming?: boolean;
}

export type ReplyTone = 'coral' | 'gardenia';

const heldRowSx = ({ isLeaving, isReturning }: HeldRow) => ({
  position: 'sticky',
  top: 0,
  zIndex: ABOVE_DIM_OVERLAY_Z_INDEX,
  ...(isLeaving && {
    animation: `${LEAVE_ANIMATION} ${HELD_LEAVE_MS}ms ease-in-out forwards`,
    [`@keyframes ${LEAVE_ANIMATION}`]: {
      '0%': { opacity: 1, transform: 'none' },
      '40%': { opacity: 1, transform: 'scale(0.98)' },
      '75%': { opacity: 1 },
      '100%': {
        opacity: 0,
        transform: 'translateY(calc(-100% - 16px)) scale(0.98)',
      },
    },
  }),
  ...(isReturning && {
    animation: `${RETURN_ANIMATION} ${HELD_RETURN_MS}ms ease-in-out ${HELD_RETURN_DELAY_MS}ms backwards`,
    [`@keyframes ${RETURN_ANIMATION}`]: {
      '0%': {
        opacity: 0,
        transform: 'translateY(calc(-100% - 16px)) scale(0.98)',
      },
      '25%': { opacity: 1 },
      '60%': { opacity: 1, transform: 'scale(0.98)' },
      '100%': { opacity: 1, transform: 'none' },
    },
  }),
});

/** Tells the held row whether it is stuck, as the chat scrolls or grows. */
const useStuck = (node: HTMLElement | null, held?: HeldRow) => {
  const onStuck = held?.onStuck;
  useLayoutEffect(() => {
    if (!node || !onStuck) return;
    const scroller = scrollerOf(node);
    if (!scroller) return;
    const measure = () => onStuck(isStuckRow(node, scroller));
    measure();
    // Again a frame later, once the chat has scrolled to its end.
    const frame = requestAnimationFrame(measure);
    scroller.addEventListener('scroll', measure, { passive: true });
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    observer?.observe(scroller);
    return () => {
      cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [node, onStuck]);
};

interface ArrivalProps {
  /** Whether the thread had already been shown when this mounted. */
  isSettled: boolean;
  /** Set on the row of a card held at the top of the chat. */
  held?: HeldRow;
  children: ReactNode;
}

/** Arrival rises and fades in what lands after the thread was first shown. */
const Arrival = ({ isSettled, held, children }: ArrivalProps) => {
  // Decided on mount, so a re-render never plays it again.
  const [isArriving] = useState(isSettled);
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  useStuck(node, held);
  return (
    <Box
      ref={setNode}
      {...{ [THREAD_ROW_ATTRIBUTE]: '' }}
      sx={{
        ...(isArriving && arrivalSx),
        ...(held && heldRowSx(held)),
      }}
    >
      {children}
    </Box>
  );
};

/** True from the render after the first, when what was there has settled. */
const useIsSettled = () => {
  const [isSettled, setIsSettled] = useState(false);
  useEffect(() => {
    setIsSettled(true);
  }, []);
  return isSettled;
};

// Within this of the end, the reader is following the conversation.
const NEAR_END_PX = 96;

/** The nearest ancestor that scrolls. */
const scrollerOf = (el: HTMLElement): HTMLElement | null => {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return node;
  }
  return null;
};

/** Keeps the end in view when rows land after the last message. */
const useFollowsGrowth = () => {
  const [node, setNode] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!node || typeof ResizeObserver === 'undefined') return;
    const scroller = scrollerOf(node);
    if (!scroller) return;

    // From zero, so rows already past the last message are brought into view.
    let height = 0;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      const growth = node.offsetHeight - height;
      height = node.offsetHeight;
      const fromEnd =
        scroller.scrollHeight -
        growth -
        scroller.scrollTop -
        scroller.clientHeight;
      if (growth <= 0 || fromEnd >= NEAR_END_PX) return;
      // A frame later, so it comes after the chat scrolls to the last message.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        scroller.scrollTop = scroller.scrollHeight;
      });
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [node]);

  return setNode;
};

type ThreadContentProps = HTMLAttributes<HTMLDivElement> & {
  footer?: ReactNode;
  endSpace?: number;
  isSettled?: boolean;
  /** The row of a card held at the top of the chat, by its message. */
  held?: HeldRow;
  /** Dims the thread under the held card; omit for no dim overlay. */
  isDimming?: boolean;
  /** The list's density, meant for its default slot rather than the DOM. */
  ownerState?: unknown;
};

/** ThreadContent is the message list's content, with the footer after the messages. */
const ThreadContent = forwardRef<HTMLDivElement, ThreadContentProps>(
  (
    {
      footer,
      endSpace = 0,
      isSettled = false,
      held,
      isDimming,
      children,
      ownerState: _ownerState,
      ...rest
    },
    ref,
  ) => {
    const followGrowth = useFollowsGrowth();
    const setRef = useCallback(
      (el: HTMLDivElement | null) => {
        followGrowth(el);
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      },
      [followGrowth, ref],
    );

    return (
      <Box
        ref={setRef}
        {...rest}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          // The gap under the bar is in here, so the dim overlay covers it.
          pt: 3,
          pb: (theme) => `calc(${theme.spacing(1)} + ${endSpace}px)`,
          // Inside the chat's own stacking, so the held row can rise above it.
          ...(isDimming !== undefined && {
            position: 'relative',
            minHeight: '100%',
          }),
        }}
      >
        {isDimming !== undefined && <DimOverlay isShown={isDimming} />}
        {Children.map(children, (row) => (
          // Each row is a message, keyed by its id.
          <Arrival
            isSettled={isSettled}
            held={
              held && isValidElement(row) && row.key === held.id
                ? held
                : undefined
            }
          >
            {row}
          </Arrival>
        ))}
        {footer && <Arrival isSettled={isSettled}>{footer}</Arrival>}
      </Box>
    );
  },
);
ThreadContent.displayName = 'ThreadContent';

/** The scrolling element inside the chat. */
const findScroller = (root: HTMLElement): HTMLElement | undefined =>
  Array.from(root.querySelectorAll<HTMLElement>('*')).find((el) => {
    const overflow = getComputedStyle(el).overflowY;
    return (
      (overflow === 'auto' || overflow === 'scroll') &&
      el.scrollHeight > el.clientHeight
    );
  });

/** Scrolls to the end on resize; the chat only auto-scrolls on new messages. */
const usePinnedToEnd = () => {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      const scroller = findScroller(root);
      if (!scroller) return;
      scroller.style.overscrollBehavior = 'contain';
      scroller.scrollTop = scroller.scrollHeight;
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  return rootRef;
};

/** MessageThread renders the accumulated conversation. */
export const MessageThread = ({
  messages,
  isTyping = false,
  onOpenDocument,
  getDocumentUrl,
  onResend,
  footer,
  endSpace,
  onEditCard,
  onPinCard,
  replyTone = 'coral',
  openMenuId,
  onChooseFromMenu,
  onChangeMenuAnswer,
  onExplain,
  isVoiceTextShown,
  onToggleVoiceText,
  heldCardId,
  isHeldDimming,
}: MessageThreadProps) => {
  const muiMessages = useMuiChatMessages(messages, isTyping);
  const heldCard = useHeldCard(heldCardId);
  const rootRef = usePinnedToEnd();
  const isSettled = useIsSettled();

  const renderVoice: DataPartRenderer = useCallback(
    ({ part, message }) => {
      const d = voicePartData(part);
      if (!d) return null;
      const isFailed = message.status === 'error';
      return (
        <PlayableVoiceNote
          durationSeconds={d.durationSeconds}
          levels={d.levels}
          documentId={d.documentId}
          isSending={!d.documentId && !isFailed}
          isFailed={isFailed}
          getDocumentUrl={getDocumentUrl}
          transcript={d.transcript}
          isTranscriptShown={isVoiceTextShown}
          onToggleTranscript={onToggleVoiceText}
        />
      );
    },
    [getDocumentUrl, isVoiceTextShown, onToggleVoiceText],
  );

  const renderMenu: DataPartRenderer = useCallback(
    ({ part, message }) => {
      const d = menuPartData(part);
      if (!d) return null;
      const canChange = d.isChangeable && !!onChangeMenuAnswer;
      return (
        <ChatMenuMessage
          text={d.text}
          options={d.options}
          pickedId={d.pickedId}
          isClosed={d.isClosed}
          onChoose={message.id === openMenuId ? onChooseFromMenu : undefined}
          onChange={
            canChange
              ? (option) => onChangeMenuAnswer(message.id, option)
              : undefined
          }
          onExplain={onExplain}
        />
      );
    },
    [onChangeMenuAnswer, onChooseFromMenu, onExplain, openMenuId],
  );

  const renderCard: DataPartRenderer = useCallback(
    ({ part, message }) => {
      const d = cardPartData(part);
      if (!d) return null;
      const { isEditable, isPinnable, ...card } = d;
      const isHeld = message.id === heldCardId;
      const isHoldable = heldCard.isHoldable(message.id);
      return (
        <ChatCard
          {...card}
          isPinned={isHeld}
          isLifted={isHoldable ? heldCard.isLifted(message.id) : undefined}
          onEdit={
            isEditable && onEditCard ? () => onEditCard(message.id) : undefined
          }
          onPin={
            isPinnable && onPinCard ? () => onPinCard(message.id) : undefined
          }
        />
      );
    },
    [heldCard.isHoldable, heldCard.isLifted, heldCardId, onEditCard, onPinCard],
  );

  const renderFile: DataPartRenderer = useCallback(
    ({ part, message }) => {
      const d = filePartData(part);
      if (!d) return null;
      const isFailed = message.status === 'error';
      const isSending = !d.documentId && !isFailed;
      const onOpen =
        d.documentId && onOpenDocument
          ? () => onOpenDocument(d.documentId!)
          : undefined;
      return (
        <Stack spacing={0.75} sx={{ alignItems: 'flex-end' }}>
          {isPhoto(d.contentType) ? (
            <PhotoAttachmentBubble
              documentId={d.documentId}
              isSending={isSending}
              isFailed={isFailed}
              getDocumentUrl={getDocumentUrl}
              onOpen={onOpen}
            />
          ) : (
            <FileAttachmentBubble
              name={d.name}
              isSending={isSending}
              isFailed={isFailed}
            />
          )}
        </Stack>
      );
    },
    [getDocumentUrl, onOpenDocument],
  );

  const partRenderers = useMemo<ChatBoxProps['partRenderers']>(
    () => ({
      'data-voice': renderVoice,
      'data-file': renderFile,
      'data-card': renderCard,
      'data-menu': renderMenu,
      'data-typing': TypingDots,
    }),
    [renderVoice, renderFile, renderCard, renderMenu],
  );

  const failedNoteProps = useMemo<FailedMessageNoteProps>(
    () => ({ onResend }),
    [onResend],
  );

  const listProps = useMemo<ChatBoxSlotProps['messageList']>(
    () => ({
      slots: { messageListContent: ThreadContent },
      // `footer` and `isSettled` are ours, so the slot props' type does not
      // list them.
      slotProps: {
        messageListContent: {
          footer,
          endSpace,
          isSettled,
          held: heldCard.row,
          // Only while a card is held, so the thread is otherwise untouched.
          isDimming: heldCardId ? isHeldDimming : undefined,
        } as object,
      },
    }),
    [endSpace, footer, heldCard.row, heldCardId, isHeldDimming, isSettled],
  );

  return (
    <Box
      ref={rootRef}
      sx={{
        flex: '1 1 0',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ChatBox
        className={
          replyTone === 'gardenia' ? GARDENIA_REPLIES_CLASS : undefined
        }
        adapter={noopChatAdapter}
        messages={muiMessages}
        initialConversations={[{ id: 'form', title: 'Conversational form' }]}
        initialActiveConversationId='form'
        partRenderers={partRenderers}
        slots={{
          conversationList: EmptySlot,
          conversationHeader: EmptySlot,
          composerRoot: EmptySlot,
          messageAvatar: null,
          messageAuthorName: null,
          messageContent: AttachmentMessageContent,
          messageMeta: null,
          // Every message has a status, which would reserve a meta row.
          messageInlineMeta: null,
          messageError: FailedMessageNote,
        }}
        slotProps={{
          messageError: failedNoteProps,
          messageList: listProps,
        }}
        features={{ conversationList: false }}
        sx={{ flex: '1 1 0', height: 0, overflow: 'hidden' }}
      />
    </Box>
  );
};

MessageThread.displayName = 'MessageThread';
export default MessageThread;
